import { Link } from 'react-router-dom';
import { useState } from 'react';
import type { ThumbnailRef } from '@pb/shared';
import { retryThumbnails } from '../api/thumbnails';
import { errorMessage } from '../api/client';

interface ThumbProps {
  thumb: ThumbnailRef | null | undefined;
  alt: string;
  badge?: string;
  hidden?: boolean;
  size?: 'sm' | 'xs';
  onRetried?: () => void;
}

export function Thumb({ thumb, alt, badge, hidden, size, onRetried }: ThumbProps) {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  const cls = `thumb${size ? ` thumb-${size}` : ''}`;

  const retry = async () => {
    if (!thumb) return;
    setRetrying(true);
    setRetryError(null);
    try {
      await retryThumbnails(thumb.owner_type, thumb.owner_id);
      setBroken(false);
      onRetried?.();
    } catch (err) {
      setRetryError(errorMessage(err));
    } finally {
      setRetrying(false);
    }
  };

  let content;
  if (!thumb) {
    content = (
      <div className="thumb-placeholder">
        <span>No preview</span>
      </div>
    );
  } else if (thumb.status === 'ready' && !broken) {
    content = <img src={thumb.url} alt={alt} loading="lazy" onError={() => setBroken(true)} />;
  } else if (thumb.status === 'pending') {
    content = (
      <div className="thumb-placeholder" role="status">
        <div className="spinner" aria-hidden="true" />
        {size === 'xs' ? null : <span>Preview is being rendered…</span>}
      </div>
    );
  } else {
    content = (
      <div className="thumb-placeholder thumb-failed">
        {size === 'xs' || (size === 'sm' && (hidden || badge)) ? null : (
          <span>{broken ? 'Preview unavailable' : 'Preview failed'}</span>
        )}
        <button
          type="button"
          className="btn btn-sm"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void retry();
          }}
          disabled={retrying}
          aria-label={`Retry preview for ${alt}`}
        >
          {retrying ? 'Retrying…' : 'Retry'}
        </button>
        {retryError ? <span>{retryError}</span> : null}
      </div>
    );
  }

  return (
    <div className={cls}>
      {content}
      {hidden || badge ? (
        <div className="thumb-badges">
          {hidden ? <span className="thumb-badge thumb-badge-hidden">Hidden</span> : null}
          {badge ? <span className="thumb-badge thumb-badge-end">{badge}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

export function ThumbLink({ to, ...props }: ThumbProps & { to: string }) {
  if (props.thumb?.status === 'failed') return <Thumb {...props} />;
  return (
    <Link to={to} tabIndex={-1} aria-hidden="true">
      <Thumb {...props} />
    </Link>
  );
}
